import React, { useState, useEffect } from 'react';

// MacOS-style control buttons
const ControlButtons = () => (
  <div className="flex space-x-2">
    <div className="w-3 h-3 rounded-full bg-red-500 border border-red-500 shadow-sm"></div>
    <div className="w-3 h-3 rounded-full bg-yellow-400 border border-yellow-400 shadow-sm"></div>
    <div className="w-3 h-3 rounded-full bg-green-500 border border-green-500 shadow-sm"></div>
  </div>
);

// Single advertisement component with MacOS-style window
const AdCard = ({ title, description, isCompanyAd, isActive }) => {
  return (
    <div 
      className={`mb-4 bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 ad-card transition-all duration-500 ease-in-out ${
        isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
      }`}
    >
      {/* Window header */}
      <div className="bg-gray-100 px-3 py-2 border-b border-gray-200 flex items-center">
        <ControlButtons />
      </div>
      
      {/* Content */}
      <div className="p-3">
        <div className="flex flex-col">
          <div className={`${isCompanyAd ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'} px-2 py-1 rounded text-xs mb-2 animate-pulse`}>
            {isCompanyAd ? 'Company' : 'Featured'} Ad
          </div>
          
          {/* Ad Image */}
          <div className="w-full h-24 bg-gray-200 rounded mb-2 flex items-center justify-center overflow-hidden">
            <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer"></div>
            <span className="text-gray-500 text-xs absolute">Ad Image</span>
          </div>
          
          <h3 className="font-bold text-sm text-gray-800">{title}</h3>
          <p className="text-xs text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  );
};

// Mock API call to fetch ads
const useAds = () => {
  const [ads, setAds] = useState({ companyAds: [], customerAds: [], loading: true });

  useEffect(() => {
    const fetchAds = async () => {
      try {
        // Simulate network request
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock API response
        const response = {
          companyAds: [
            {
              title: "Premium Membership",
              description: "Upgrade your account for exclusive features and benefits."
            },
            {
              title: "New Blog Features",
              description: "Discover our latest blogging tools and features."
            },
            {
              title: "Pro Analytics",
              description: "Track your performance with our advanced analytics."
            }
          ],
          customerAds: [
            {
              title: "Summer Sale",
              description: "Get 50% off on all summer products. Limited time offer!"
            },
            {
              title: "Learn Web Development",
              description: "Online courses starting from $9.99 only!"
            },
            {
              title: "Travel Packages",
              description: "Exclusive deals on luxury vacation packages."
            }
          ]
        };
        
        setAds({ 
          companyAds: response.companyAds, 
          customerAds: response.customerAds,
          loading: false
        });
      } catch (error) {
        // Fallback to sample data
        setAds({ 
          companyAds: companyAds,
          customerAds: customerAds,
          loading: false
        });
      }
    };
    
    fetchAds();
  }, []);
  
  return ads;
};

// Sample fallback data
const companyAds = [
  {
    title: "Premium Membership",
    description: "Upgrade your account for exclusive features and benefits."
  },
  {
    title: "New Blog Features",
    description: "Discover our latest blogging tools and features."
  }
];

const customerAds = [
  {
    title: "Summer Sale",
    description: "Get 50% off on all summer products. Limited time offer!"
  },
  {
    title: "Learn Web Development",
    description: "Online courses starting from $9.99 only!"
  }
];

// Scroll container for ads
const ScrollableAdContainer = ({ title, ads, isCompanyAd }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Auto-rotate ads every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prevIndex => (prevIndex + 1) % ads.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [ads.length]);
  
  return (
    <div className="ad-container">
      <h2 className="text-lg font-bold mb-3 text-gray-700">{title}</h2>
      
      {/* Scroll container */}
      <div className="h-64 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {ads.map((ad, index) => (
          <AdCard 
            key={`ad-${index}`}
            title={ad.title}
            description={ad.description}
            isCompanyAd={isCompanyAd}
            isActive={index === activeIndex}
          />
        ))}
      </div>
      
      {/* Navigation dots */}
      <div className="flex justify-center mt-2">
        {ads.map((_, index) => (
          <button
            key={`dot-${index}`}
            className={`w-2 h-2 mx-1 rounded-full ${index === activeIndex ? 'bg-blue-500' : 'bg-gray-300'}`}
            onClick={() => setActiveIndex(index)}
            aria-label={`View ad ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Main Ad Container component
const Advertisement = () => {
  const { companyAds, customerAds, loading } = useAds();
  const [isVisible, setIsVisible] = useState(false);
  const [isSticky, setIsSticky] = useState(true);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  // Detect when footer is approaching and switch from fixed to absolute
  useEffect(() => {
    const handleScroll = () => {
      const footer = document.querySelector('footer');
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        
        // If footer is about to enter viewport (within 400px), stop sticky behavior
        if (footerTop <= windowHeight + 400) {
          setIsSticky(false);
        } else {
          setIsSticky(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <>
      {/* Left sidebar ads */}
      <div 
        className={`${isSticky ? 'fixed' : 'absolute'} left-0 w-48 z-40 px-4 ad-left transition-all duration-700 ${
          isVisible ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
        }`}
        style={{
          top: isSticky ? '96px' : 'auto',
          bottom: isSticky ? 'auto' : '20px'
        }}
      >
        {loading ? (
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
            <div className="h-40 bg-gray-100 rounded mb-4"></div>
            <div className="h-40 bg-gray-100 rounded"></div>
          </div>
        ) : (
          <ScrollableAdContainer 
            title="Company Offers" 
            ads={companyAds} 
            isCompanyAd={true} 
          />
        )}
      </div>
      
      {/* Right sidebar ads */}
      <div 
        className={`${isSticky ? 'fixed' : 'absolute'} right-0 w-48 z-40 px-4 ad-right transition-all duration-700 ${
          isVisible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
        }`}
        style={{
          top: isSticky ? '96px' : 'auto',
          bottom: isSticky ? 'auto' : '20px'
        }}
      >
        {loading ? (
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
            <div className="h-40 bg-gray-100 rounded mb-4"></div>
            <div className="h-40 bg-gray-100 rounded"></div>
          </div>
        ) : (
          <ScrollableAdContainer 
            title="Featured Ads" 
            ads={customerAds} 
            isCompanyAd={false} 
          />
        )}
      </div>
      
      {/* Add global styles */}
      <style jsx global>{`
        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        
        .animate-shimmer {
          background-size: 400px 100%;
          animation: shimmer 1.5s infinite linear;
        }
      `}</style>
    </>
  );
};

export default Advertisement;
